import { useState } from "react";
import { clsx } from "clsx";
import { CheckCircle2, ChevronDown, Circle, Users } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { useAnnouncementAudience } from "../hooks/useWhatsNew";
import type { AnnouncementRecipient } from "../types";

function formatReadAt(value: string | null): string {
  if (!value) return "";
  return new Date(value).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function RecipientRow({ r }: { r: AnnouncementRecipient }) {
  return (
    <li className="flex items-start justify-between gap-3 py-1.5 px-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800/60">
      <div className="min-w-0">
        <span className="text-xs text-gray-900 dark:text-gray-100">{r.name}</span>
        <span className="ml-1.5 text-[10px] text-gray-400">
          {r.job_title || r.role_label}
          {r.access_reason === "manager" && " · dostęp stały"}
        </span>
      </div>
      {r.is_read && (
        <span className="text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap tabular-nums">
          {formatReadAt(r.read_at)}
        </span>
      )}
    </li>
  );
}

function Column({
  title,
  icon,
  tone,
  people,
  empty,
}: {
  title: string;
  icon: React.ReactNode;
  tone: "read" | "unread";
  people: AnnouncementRecipient[];
  empty: string;
}) {
  return (
    <div className="min-w-0">
      <h4
        className={clsx(
          "flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider mb-1.5",
          tone === "read"
            ? "text-green-700 dark:text-green-400"
            : "text-gray-500 dark:text-gray-400",
        )}
      >
        {icon}
        {title} ({people.length})
      </h4>
      {people.length === 0 ? (
        <p className="text-xs text-gray-400 px-2 py-1.5">{empty}</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {people.map((r) => (
            <RecipientRow key={r.id} r={r} />
          ))}
        </ul>
      )}
    </div>
  );
}

/** Collapsible „kto ma dostęp" panel with a read / not-read split. Managers only. */
export function AnnouncementAudience({
  announcementId,
  isPublished,
}: {
  announcementId: string;
  isPublished: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useAnnouncementAudience(announcementId, open);

  const pct = data && data.total > 0 ? Math.round((data.read_count / data.total) * 100) : 0;

  return (
    <div className="mt-3 border-t border-gray-100 dark:border-gray-800 pt-2">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
      >
        <ChevronDown
          size={14}
          className={clsx("transition-transform shrink-0", open && "rotate-180")}
        />
        <Users size={13} className="shrink-0" />
        <span>
          {isPublished ? "Kto ma dostęp" : "Kto będzie mieć dostęp po publikacji"}
          {data ? ` (${data.total})` : ""}
        </span>
        {data && isPublished && (
          <>
            <span className="ml-auto text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
              Odczytane {data.read_count}/{data.total}
            </span>
            <span className="w-16 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden shrink-0">
              <span className="block h-full bg-green-500" style={{ width: `${pct}%` }} />
            </span>
          </>
        )}
      </button>

      {open && (
        <div className="mt-2">
          {isLoading || !data ? (
            <div className="flex justify-center py-4">
              <Spinner />
            </div>
          ) : !isPublished ? (
            // A draft has no meaningful read receipts yet — just the prospective list.
            <ul className="divide-y divide-gray-100 dark:divide-gray-800">
              {[...data.read, ...data.unread].map((r) => (
                <RecipientRow key={r.id} r={{ ...r, is_read: false }} />
              ))}
            </ul>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <Column
                title="Odczytali"
                icon={<CheckCircle2 size={12} />}
                tone="read"
                people={data.read}
                empty="Nikt jeszcze nie odczytał."
              />
              <Column
                title="Nie odczytali"
                icon={<Circle size={12} />}
                tone="unread"
                people={data.unread}
                empty="Wszyscy odczytali."
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
